import { Search } from 'lucide-react'
import { useMemo, useState } from 'react'

import { DetailSection } from './detail-section'
import {
  formatQuota,
  getPricingTierRows,
  groupCapabilities,
  isCapabilityRow,
  isLimitRow,
  PricingTierRow
} from './pricing-tier-rows'

import { PricingTierComponents } from '@/api'
import { Segmented } from '@/components/activity/activity-panel'
import { Button } from '@/components/ui/button'
import { Icons } from '@/components/ui/icons'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'

type PricingTier = PricingTierComponents.Schemas.PricingTier

type Filter = 'all' | 'overridden'

type PricingTierTabProps = {
  pricingTiers?: PricingTier[]
  selectedPricingTierId?: string
  onSelectPricingTier: (pricingTierId: string) => void
  /** the selected tier with this org's overrides, as edited */
  pricingTier?: PricingTier
  /** `undefined` removes the override, so the tier value applies again */
  onOverrideChange: (key: string, value: unknown) => void
  onResetOverrides: () => void
  loading?: boolean
  disabled?: boolean
}

export const PricingTierTab = ({
  pricingTiers,
  selectedPricingTierId,
  onSelectPricingTier,
  pricingTier,
  onOverrideChange,
  onResetOverrides,
  loading,
  disabled
}: PricingTierTabProps) => {
  const [filter, setFilter] = useState<Filter>('all')
  const [query, setQuery] = useState('')

  const rows = useMemo(() => getPricingTierRows(pricingTier), [pricingTier])
  const overriddenCount = rows.filter((row) => row.overridden).length
  const capabilities = rows.filter(isCapabilityRow)
  const enabledCount = capabilities.filter((row) => row.value?.enabled).length

  const search = query.trim().toLowerCase()
  const visible = rows.filter(
    (row) =>
      (filter === 'all' || row.overridden) &&
      (!search ||
        row.label.toLowerCase().includes(search) ||
        row.key.includes(search))
  )
  const limits = visible.filter(isLimitRow)
  const groups = groupCapabilities(visible.filter(isCapabilityRow))

  const toggleCapability = (row: PricingTierRow, enabled: boolean) =>
    onOverrideChange(
      row.key,
      enabled === Boolean(row.tierValue?.enabled) ? undefined : { enabled }
    )

  const changeLimit = (row: PricingTierRow, raw: string) => {
    const quota = parseInt(raw, 10)

    onOverrideChange(row.key, Number.isNaN(quota) ? undefined : { quota })
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-5 rounded-lg border bg-card p-5">
        <div className="w-full max-w-md space-y-2">
          <Label htmlFor="pricing_tier">Pricing tier</Label>
          <Select
            disabled={disabled}
            onValueChange={onSelectPricingTier}
            value={selectedPricingTierId}
          >
            <SelectTrigger id="pricing_tier">
              <SelectValue placeholder="Select a pricing tier" />
            </SelectTrigger>
            <SelectContent>
              {pricingTiers?.map((tier) => (
                <SelectItem key={tier.id} value={tier.id!}>
                  {tier.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            Switching the tier clears this organization&apos;s overrides. Saving
            also updates the root role&apos;s grants.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-6">
          <div>
            <p className="text-xs text-muted-foreground">Capabilities on</p>
            <p className="text-xl font-semibold tabular-nums">
              {enabledCount} / {capabilities.length}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Overrides</p>
            <p
              className={cn(
                'text-xl font-semibold tabular-nums',
                overriddenCount > 0 && 'text-amber-600 dark:text-amber-400'
              )}
            >
              {overriddenCount}
            </p>
          </div>
          <Button
            disabled={disabled || overriddenCount === 0}
            onClick={onResetOverrides}
            size="sm"
            variant="outline"
          >
            Reset all overrides
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="relative w-full max-w-sm">
          <Search
            aria-hidden
            className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            aria-label="Search settings"
            className="pl-9"
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search settings"
            value={query}
          />
        </div>
        <Segmented
          ariaLabel="Show settings"
          onChange={setFilter}
          options={[
            { value: 'all', label: `All settings · ${rows.length}` },
            { value: 'overridden', label: `Overridden · ${overriddenCount}` }
          ]}
          value={filter}
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Icons.spinner className="h-6 w-6" />
        </div>
      ) : (
        <>
          {limits.length > 0 && (
            <DetailSection
              bodyClassName="p-0"
              description="Leave a value empty to use the tier default. -1 means unlimited."
              title="Limits"
            >
              <div className="overflow-x-auto">
                <table className="w-full min-w-[560px] text-sm">
                  <thead>
                    <tr className="border-b text-left text-xs text-muted-foreground">
                      <th className="px-5 py-2.5 font-medium" scope="col">
                        Setting
                      </th>
                      <th className="w-40 px-3 py-2.5 font-medium" scope="col">
                        Tier default
                      </th>
                      <th className="w-48 px-3 py-2.5 font-medium" scope="col">
                        This organization
                      </th>
                      <th className="w-24 px-5 py-2.5" scope="col">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {limits.map((row) => (
                      <tr
                        className={cn(
                          'border-b border-border/60 last:border-0',
                          row.overridden && 'bg-amber-500/5'
                        )}
                        key={row.key}
                      >
                        <th
                          className="px-5 py-2 text-left font-normal"
                          scope="row"
                        >
                          <label htmlFor={`limit-${row.key}`}>
                            {row.label}
                          </label>
                        </th>
                        <td className="px-3 py-2 text-muted-foreground">
                          {formatQuota(row.tierValue)}
                        </td>
                        <td className="px-3 py-2">
                          <Input
                            className={cn(
                              'h-9 w-36',
                              row.overridden && 'border-amber-500/60'
                            )}
                            disabled={disabled}
                            id={`limit-${row.key}`}
                            min={row.key === 'active_journeys' ? -1 : 0}
                            onChange={(e) => changeLimit(row, e.target.value)}
                            placeholder={String(row.tierValue?.quota ?? '')}
                            type="number"
                            value={row.override?.quota ?? ''}
                          />
                        </td>
                        <td className="px-5 py-2 text-right">
                          {row.override !== undefined && (
                            <Button
                              disabled={disabled}
                              onClick={() =>
                                onOverrideChange(row.key, undefined)
                              }
                              size="sm"
                              variant="ghost"
                            >
                              Reset
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </DetailSection>
          )}

          {groups.length > 0 && (
            <DetailSection
              description="Grouped by area. Rows marked Override differ from the tier."
              title="Capabilities"
            >
              <div className="space-y-5">
                {groups.map((group) => (
                  <div className="space-y-2" key={group.label}>
                    <h3 className="text-xs font-semibold text-muted-foreground">
                      {group.label}
                    </h3>
                    <div className="grid gap-2 md:grid-cols-2 2xl:grid-cols-3">
                      {group.rows.map((row) => (
                        <div
                          className={cn(
                            'flex min-h-[3rem] items-center justify-between gap-3 rounded-md border px-3 py-2',
                            row.overridden &&
                              'border-amber-500/60 bg-amber-500/5'
                          )}
                          key={row.key}
                        >
                          <div className="min-w-0">
                            <Label htmlFor={`capability-${row.key}`}>
                              {row.label}
                            </Label>
                            {row.overridden && (
                              <p className="text-xs text-amber-700 dark:text-amber-400">
                                Override · tier:{' '}
                                {row.tierValue?.enabled ? 'On' : 'Off'}
                              </p>
                            )}
                          </div>
                          <Switch
                            checked={Boolean(row.value?.enabled)}
                            disabled={disabled}
                            id={`capability-${row.key}`}
                            onCheckedChange={(enabled) =>
                              toggleCapability(row, enabled)
                            }
                            style={{ marginTop: 0 }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </DetailSection>
          )}

          {limits.length === 0 && groups.length === 0 && (
            <p className="py-8 text-center text-sm text-muted-foreground">
              {filter === 'overridden' && !search
                ? 'This organization uses the tier as is.'
                : 'No settings match your search.'}
            </p>
          )}
        </>
      )}
    </div>
  )
}
